import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { ARTICLES } from "../constants/blogData";
import Reveal from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";

const LOCALES = { en: "en-GB", fr: "fr-FR", nl: "nl-NL", ar: "ar" };

export default function BlogArticlePage() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const locale = LOCALES[lang] || "en-GB";
  const article = ARTICLES.find((a) => a.slug === slug);
  const c = article ? article.t[lang] || article.t.en : null;
  const lp = lang === "en" ? "" : `/${lang}`;

  useSeo(
    c ? `${c.title} | MIRAL GROUP` : "MIRAL GROUP Journal",
    c ? c.excerpt : "",
    c
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: c.title,
          description: c.excerpt,
          datePublished: article.date,
          inLanguage: lang,
          author: { "@type": "Organization", name: "MIRAL GROUP" }
        }
      : null
  );

  if (!article) return <Navigate to="/blog" replace />;

  return (
    <main data-testid="blog-article-page" className="pt-[72px] min-h-screen">
      <article className="mx-auto max-w-3xl px-5 lg:px-10 py-16 lg:py-24">
        <Reveal>
          <Link to={`${lp}/blog`} data-testid="blog-back-link" className="inline-flex items-center gap-2 font-mono2 text-[11px] uppercase text-slate-400 hover:text-gold-soft transition-colors">
            <ArrowLeft size={14} /> {t.blog.back}
          </Link>
          <p className="mt-8 font-mono2 text-[10px] uppercase text-slate-500">
            {new Date(article.date).toLocaleDateString(locale, { dateStyle: "long" })} · {article.minutes} {t.blog.minRead}
          </p>
          <h1 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
            {c.title}
          </h1>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10">
            <img src={article.image} alt={c.title} className="h-64 lg:h-80 w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070709]/70 via-transparent to-transparent" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 space-y-6">
            {c.body.map((p, i) => (
              <p key={i} className="text-base sm:text-lg text-slate-300 leading-[1.8]">
                {p}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-14 rounded-2xl border border-gold/25 bg-gradient-to-br from-[#12141C] to-[#0a0b0e] p-8 text-center">
            <p className="font-display text-lg font-bold">{t.servicesPage.ctaTitle}</p>
            <Link to={`${lp}/contact`} data-testid="blog-article-cta" className="btn-gold mt-5 inline-flex text-[11px] font-mono2 uppercase">
              {t.servicesPage.ctaButton} <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
      </article>
    </main>
  );
}
